import { createTheme } from "@mui/material";

// New color palette - Teal to Cyan
export const brandPrimary = "#006d77";  // Dark teal
export const brandPrimaryLight = "#83c5be"; // Light teal
export const brandPrimaryDark = "#004c54"; // Darker teal
export const brandSecondary = "#00b4d8"; // Bright cyan
export const brandSecondaryLight = "#90e0ef"; // Light cyan
export const brandSecondaryDark = "#0077b6"; // Dark cyan

// Cabin indicator color for LG
export const cabinBlueLG = "#0077b6";

// Glassmorphism constants for light mode
const lightGlassEffect = {
    background: "rgba(255, 255, 255, 0.65)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
};

// Glassmorphism constants for dark mode
const darkGlassEffect = {
    background: "rgba(18, 24, 38, 0.7)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
};

const commonThemeOptions = {
    shape: { borderRadius: 16 },
    typography: {
        fontFamily: '"Inter", "Roboto", sans-serif',
        h3: { fontWeight: 700 },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    transition: "all 0.3s ease",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                },
            },
        },
    },
};

const lightTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: brandPrimary,
            light: brandPrimaryLight,
            dark: brandPrimaryDark,
            contrastText: "#fff",
        },
        secondary: {
            main: brandSecondary,
            light: brandSecondaryLight,
            dark: brandSecondaryDark,
            contrastText: "#fff",
        },
        background: {
            default: "#f8f9fa",
            paper: "rgba(255, 255, 255, 0.9)",
        },
        text: {
            primary: "#2c3e50",
            secondary: "#546e7a",
        },
        success: { main: "#2ecc71", contrastText: "#fff" },
        error: { main: "#e74c3c", contrastText: "#fff" },
        warning: { main: "#f39c12", contrastText: "#fff" },
        info: { main: "#3498db", contrastText: "#fff" },
    },
    ...commonThemeOptions,
    components: {
        ...commonThemeOptions.components,
        MuiCard: {
            styleOverrides: {
                root: {
                    ...lightGlassEffect,
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                        background: "rgba(255, 255, 255, 0.75)",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...lightGlassEffect,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                },
            },
        },
    },
});

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: brandPrimaryLight,
            light: brandSecondaryLight,
            dark: brandPrimary,
            contrastText: "#fff",
        },
        secondary: {
            main: brandSecondaryLight,
            light: "#caf0f8",
            dark: brandSecondary,
            contrastText: "#fff",
        },
        background: {
            default: "#0A1929",
            paper: "rgba(41, 43, 46, 0.7)",
        },
        text: {
            primary: "#ffffff",
            secondary: "rgba(255, 255, 255, 0.7)",
        },
        success: { main: "#2ecc71", contrastText: "#fff" },
        error: { main: "#e74c3c", contrastText: "#fff" },
        warning: { main: "#f39c12", contrastText: "#fff" },
        info: { main: "#3498db", contrastText: "#fff" },
    },
    ...commonThemeOptions,
    components: {
        ...commonThemeOptions.components,
        MuiCard: {
            styleOverrides: {
                root: {
                    ...darkGlassEffect,
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
                        background: "rgba(18, 24, 38, 0.8)",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...darkGlassEffect,
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                },
            },
        },
    },
});

export { lightTheme, darkTheme };